import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { formatHorizonSlug } from '../utils/formatHorizonSlug';
import './SuffixSelector.css';

function SuffixSelector() {
  const { user } = useAuth();
  const [horizons, setHorizons] = useState([]);
  const [currentHorizon, setCurrentHorizon] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      loadUserHorizons();
    }
  }, [user]);

  const loadUserHorizons = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/user-suffixes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setHorizons(data.suffixes || []);
        setCurrentHorizon(data.currentSuffix || '');
      } else {
        setMessage({ type: 'error', text: 'Failed to load available horizons' });
      }
    } catch (error) {
      console.error('Error loading user horizons:', error);
      setMessage({ type: 'error', text: 'Error loading horizons' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (suffix) => {
    if (suffix === currentHorizon) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/set-active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ suffix }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCurrentHorizon(suffix);
        setMessage({ type: 'success', text: `Active horizon set to ${formatHorizonSlug(suffix)}` });
        // Persist first, then full reload — horizon switch invalidates groups, tools, sessions, and caches.
        window.dispatchEvent(new CustomEvent('horizonChanged', {
          detail: { suffix, previousSuffix: currentHorizon },
        }));
        window.location.reload();
        return;
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to set active horizon' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error setting active horizon:', error);
      setMessage({ type: 'error', text: 'Error setting active horizon' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="suffix-selector-loading">
        <i className="fas fa-spinner fa-spin"></i> Loading horizons...
      </div>
    );
  }

  if (horizons.length === 0) {
    return (
      <div className="suffix-selector-empty">
        <p>No horizons available. Contact an administrator to grant you access to horizons.</p>
      </div>
    );
  }

  return (
    <div className="suffix-selector">
      {message.text && (
        <div className={`suffix-message alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div className="suffix-current">
        <div className="suffix-current-label">Current Active:</div>
        <div className="suffix-current-value">
          <span className="suffix-badge active">{formatHorizonSlug(currentHorizon) || 'None'}</span>
        </div>
      </div>

      <div className="suffix-list">
        {horizons.map((horizon) => (
          <div key={horizon.suffix} className={`suffix-item ${horizon.suffix === currentHorizon ? 'active' : ''}`}>
            <div className="suffix-item-info">
              <span className="suffix-name">{formatHorizonSlug(horizon.suffix)}</span>
              {horizon.description && (
                <span className="suffix-description">{horizon.description}</span>
              )}
            </div>
            <div className="suffix-item-actions">
              {horizon.suffix === currentHorizon ? (
                <span className="suffix-active-indicator">
                  <i className="fas fa-check-circle"></i> Active
                </span>
              ) : (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleSetActive(horizon.suffix)}
                  disabled={saving}
                >
                  {saving ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <>
                      <i className="fas fa-check"></i> Set Active
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SuffixSelector;
